import React from 'react';
import { Box, Typography, Grid, IconButton, Paper, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isToday
} from 'date-fns';

const AttendanceCalendar = ({
    currentDate,
    onDateClick,
    onMonthChange,
    attendanceData = [], // Array of { date: 'yyyy-MM-dd', status: 'recorded' | 'holiday' | 'sunday' }
    holidays = [] // Array of { date: 'yyyy-MM-dd', name: '...' }
}) => {
    const theme = useTheme();

    // Navigate months
    const nextMonth = () => onMonthChange(addMonths(currentDate, 1));
    const prevMonth = () => onMonthChange(subMonths(currentDate, 1));

    // Generate calendar days
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    const weeks = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const getDayStatus = (day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const att = attendanceData.find(a => a.date === dateStr);
        if (att) return att.status;

        const hol = holidays.find(h => h.date === dateStr);
        if (hol) return 'holiday';

        // Simple sunday check if no data
        if (day.getDay() === 0) return 'sunday';

        return null;
    };

    return (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'white', mb: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={prevMonth} size="small">
                    <ChevronLeft />
                </IconButton>
                <Typography variant="h6" fontWeight="bold">
                    {format(currentDate, 'MMMM yyyy')}
                </Typography>
                <IconButton onClick={nextMonth} size="small">
                    <ChevronRight />
                </IconButton>
            </Box>

            {/* Days Header */}
            <Grid container sx={{ mb: 1 }}>
                {weeks.map((day, index) => (
                    <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 'bold',
                                display: 'block',
                                py: 1
                            }}
                        >
                            {day}
                        </Typography>
                    </Grid>
                ))}
            </Grid>

            {/* Calendar Grid */}
            <Grid container rowSpacing={1}>
                {days.map((day, index) => {
                    const status = getDayStatus(day);
                    const isSelected = isSameDay(day, currentDate);
                    const isCurrentMonth = isSameMonth(day, monthStart);
                    const isTodayDate = isToday(day);

                    let bgcolor = 'transparent';
                    let color = isCurrentMonth ? 'text.primary' : 'text.disabled';
                    let border = 'none';

                    if (isSelected) {
                        bgcolor = theme.palette.primary.main;
                        color = 'white';
                    } else if (isTodayDate) {
                        border = `1px solid ${theme.palette.primary.main}`;
                    }

                    return (
                        <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                            <Box
                                onClick={() => onDateClick(day)}
                                sx={{
                                    height: 36,
                                    width: 36,
                                    margin: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '12px',
                                    bgcolor: bgcolor,
                                    border: border,
                                    color: color,
                                    cursor: 'pointer',
                                    position: 'relative',
                                    '&:hover': {
                                        bgcolor: isSelected ? theme.palette.primary.dark : '#f5f5f5'
                                    }
                                }}
                            >
                                <Typography variant="body2" fontWeight={isSelected || isTodayDate ? 'bold' : 'normal'}>
                                    {format(day, 'd')}
                                </Typography>

                                {/* Status Dot */}
                                {!isSelected && status && (
                                    <Box
                                        sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            mt: 0.5,
                                            bgcolor:
                                                status === 'recorded' || status === 'present' ? '#4caf50' : // Green
                                                    status === 'holiday' ? '#ffc107' : // Yellow
                                                        status === 'sunday' ? '#e0e0e0' : // Grey
                                                            status === 'absent' ? '#ef5350' : // Red
                                                                'transparent'
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Legend */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                    <Typography variant="caption" color="text.secondary">Recorded</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffc107' }} />
                    <Typography variant="caption" color="text.secondary">Holiday</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default AttendanceCalendar;
